import { onUnmounted } from "vue";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";

const FAILURE_MESSAGE = "Zoom reconnect failed. Please try again.";

/** Same base URL FetchService uses. The Zoom callback page is served by that API. */
function apiOrigin(): string {
  return new URL(import.meta.env.VITE_BASE_URL).origin;
}

/**
 * Callback errors are HTML-escaped (&#039; for an apostrophe). Decode them in a
 * detached document and return text only — never assign HTML to a live element.
 */
function decodeHtmlEntities(value: string): string {
  const text = new DOMParser().parseFromString(value, "text/html").documentElement.textContent;
  return text ?? "";
}

type ZoomUrlResult = {
  data?: { url?: string };
  res?: { ok?: boolean; status?: number };
};

let detachListener: (() => void) | null = null;
let listenerOwner: symbol | null = null;

function detachActiveListener() {
  detachListener?.();
  detachListener = null;
  listenerOwner = null;
}

/**
 * Opens a fresh admin Zoom OAuth popup and waits for the callback page to
 * postMessage `{ sender: 'ptc_callback' }`. FetchService already snackbars 403/423.
 */
export function useZoomReconnect() {
  const alertsStore = useAlertsStore();
  const owner = Symbol("zoom-reconnect");

  const reconnectZoom = async () => {
    const res = (await FetchService.fetchData("/zoom/url?type=admin", "get", null, true)) as
      | ZoomUrlResult
      | false
      | null
      | undefined;

    // 401 and logged-out redirects return false and do not alert.
    if (res === false || res == null) {
      alertsStore.addAlert("error", FAILURE_MESSAGE);
      return;
    }

    // 403 Forbidden / Forbidden roles and 423 already show a FetchService snackbar.
    const status = res.res?.status;
    if (status === 403 || status === 423) return;

    const url = res.data?.url;
    if (!res.res?.ok || typeof url !== "string" || url.length === 0) {
      alertsStore.addAlert("error", FAILURE_MESSAGE);
      return;
    }

    detachActiveListener();

    let popup: Window | null = null;
    const onMessage = (event: MessageEvent) => {
      if (popup == null || event.source !== popup) return;
      if (event.origin !== apiOrigin()) return;

      const data = event.data as { sender?: string; success?: unknown; error?: unknown } | null;
      if (!data || data.sender !== "ptc_callback") return;

      detachActiveListener();
      if (data.success) {
        alertsStore.addAlert("success", "Zoom reconnected.");
        return;
      }
      const detail =
        typeof data.error === "string" ? decodeHtmlEntities(data.error).trim() : "";
      alertsStore.addAlert("error", detail || FAILURE_MESSAGE);
    };

    window.addEventListener("message", onMessage);
    detachListener = () => window.removeEventListener("message", onMessage);
    listenerOwner = owner;

    const width = 600;
    const height = 700;
    const left = Math.max(0, (window.innerWidth - width) / 2);
    const top = Math.max(0, (window.innerHeight - height) / 2);
    popup = window.open(
      url,
      "zoom_reconnect",
      `left=${left},top=${top},width=${width},height=${height}`
    );

    if (!popup) {
      detachActiveListener();
      alertsStore.addAlert("error", "Pop-up blocked. Allow pop-ups and try again.");
    }
  };

  onUnmounted(() => {
    if (listenerOwner === owner) detachActiveListener();
  });

  return { reconnectZoom };
}
